/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, computed, flow, toJS } from 'mobx';
import fetch from '../fetch';
import ErrorsStore from './errorsStore';
import moment from 'moment';
import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import reverse from 'lodash/reverse';
import _ from '../translations';
import profileStore from './profileStore';

class QuotaStore {
    @observable
    storage = [];
    @observable
    users = [];
    @observable
    traffic = [];
    @observable
    quota = [];
    @observable
    totalVideos = 0;
    @observable
    storageDaysInterval = 1;

    @action
    reset = () => {
        this.storage = [];
        this.users = [];
        this.traffic = [];
        this.quota = [];
        this.totalVideos = 0;
        this.storageDaysInterval = 1;
    };

    @action.bound
    getQuota = flow(function*() {
        if (profileStore.isRoot) return;

        const url = '/api/core/quota/last/';
        try {
            const list = yield fetch.get({
                url,
                params: {},
            });
            if (list.error) {
                ErrorsStore.addError(list, _('Failed to get used quota'));
            }
            this.quota = get(list, 'items', []);
        } catch (e) {
            ErrorsStore.addError(e, _('Failed to get used quota'));
            console.warn(e);
        }
    });

    @action.bound
    getStorage = flow(function*({
        from = moment()
            .subtract(1, 'months')
            .valueOf(),
        until = moment().valueOf(),
        period = 'daily',
    } = {}) {
        if (profileStore.isRoot) return;

        const url = `/api/core/usage/storage/${period}`;
        try {
            const list = yield fetch.get({
                url,
                params: {
                    from,
                    until,
                },
            });
            if (list.error) {
                ErrorsStore.addError(list, _('Failed to get storage usage'));
            }
            this.storage = get(list, 'items', []);
        } catch (e) {
            ErrorsStore.addError(e, _('Failed to get storage usage'));
            console.warn(e);
        }
    });

    @action.bound
    getUsers = flow(function*({
        from = moment()
            .subtract(1, 'months')
            .valueOf(),
        until = moment().valueOf(),
        period = 'daily',
    } = {}) {
        if (profileStore.isRoot) return;

        const url = `/api/core/usage/users/${period}`;
        try {
            const list = yield fetch.get({
                url,
                params: {
                    from,
                    until,
                },
            });
            if (list.error) {
                ErrorsStore.addError(list, _('Failed to get users stats'));
            }
            this.users = get(list, 'items', []);
        } catch (e) {
            ErrorsStore.addError(e, _('Failed to get users stats'));
            console.warn(e);
        }
    });

    @action.bound
    getTraffic = flow(function*({
        from = moment()
            .subtract(1, 'months')
            .valueOf(),
        until = moment().valueOf(),
        period = 'daily',
    } = {}) {
        if (profileStore.isRoot) return;

        const url = `/api/core/usage/traffic/${period}`;

        try {
            const list = yield fetch.get({
                url,
                params: {
                    from,
                    until,
                },
            });
            if (list.error) {
                ErrorsStore.addError(list, _('Failed to get traffic stats'));
            }
            this.traffic = get(list, 'items', []);
        } catch (e) {
            ErrorsStore.addError(e, _('Failed to get traffic stats'));
            console.warn(e);
        }
    });

    @action.bound
    getTotalVideos = flow(function*() {
        if (profileStore.isRoot) return;

        const url = '/api/assets/videos';
        try {
            const list = yield fetch.get({
                url,
                params: {
                    itemsPerPage: 1,
                    paginate: true,
                },
            });
            if (list.error) {
                ErrorsStore.addError(list, _('Failed to get video stats'));
            }
            this.totalVideos = get(list, 'pagination.totalItems', 0);
        } catch (e) {
            ErrorsStore.addError(e, _('Failed to get video stats'));
            console.warn(e);
        }
    });

    @computed
    get totalQuota() {
        const quota = toJS(this.quota);
        if (quota.length < 0) return false;
        return quota;
    }
    @computed
    get storageQuota() {
        const quota = toJS(this.quota);
        if (quota.length < 0) return false;
        const storage = find(quota, { type: 'storage' });
        if (!storage)
            return {
                usedPercent: 0,
                freePercent: 0,
                used: 0,
                limit: 0,
                unit: 'gb',
                limitUnit: 'gb',
            };

        let usedPercent = (storage.used / storage.limit) * 100;

        if (usedPercent < 1) {
            usedPercent = usedPercent.toFixed(2);
        } else {
            usedPercent = parseInt(usedPercent, 10);
        }

        const convertedUsed = this.convertToConvenientUnit(storage.used);
        const convertedLimit = this.convertToConvenientUnit(storage.limit);

        return {
            usedPercent,
            freePercent: 100 - usedPercent,
            used: convertedUsed.used,
            limit: convertedLimit.used,
            unit: convertedUsed.unit,
            limitUnit: convertedLimit.unit,
        };
    }

    @action
    setStorageDaysInterval(val = 1) {
        this.storageDaysInterval = val;
    }

    convertToConvenientUnit = (bytes, decimals = 2) => {
        const getBestDecimals = bytes => {
            const integer = parseInt(bytes, 10);
            const fixed = bytes.toFixed(decimals);

            if (integer - fixed === 0) return integer;
            return fixed;
        };

        const units = ['kb', 'mb', 'gb', 'tb', 'pt'];

        if (bytes < 1024) {
            return {
                unit: 'bytes',
                used: getBestDecimals(bytes),
            };
        }

        for (let i = 0; i < 4; i++) {
            bytes = bytes / 1024;

            if (bytes < 1024)
                return {
                    unit: units[i],
                    used: getBestDecimals(bytes),
                };
        }

        return {
            unit: 'pt',
            used: getBestDecimals(bytes),
        };
    };

    @computed
    get usersQuota() {
        const quota = toJS(this.quota);
        if (quota.length < 0) return false;
        const users = find(quota, { type: 'user' });
        if (!users)
            return {
                usedPercent: 0,
                freePercent: 0,
                used: 0,
                limit: 0,
            };
        return users;
    }

    @computed
    get trafficQuota() {
        const quota = toJS(this.quota);
        if (quota.length < 0) return false;
        const traffic = find(quota, { type: 'traffic' });
        if (!traffic)
            return {
                usedPercent: 0,
                freePercent: 0,
                used: 0,
                limit: 0,
                unit: 'gb',
            };

        let usedPercent = (traffic.used / traffic.limit) * 100;

        if (usedPercent >= 1) {
            usedPercent = parseInt(usedPercent, 10);
        }

        const convertedUsed = this.convertToConvenientUnit(traffic.used);
        const convertedLimit = this.convertToConvenientUnit(traffic.limit);

        return {
            usedPercent,
            freePercent: 100 - usedPercent,
            used: convertedUsed.used,
            limit: convertedLimit.used,
            unit: convertedUsed.unit,
            limitUnit: convertedLimit.unit,
        };
    }

    /**
     * This function will group traffic data in days intervals based on
     * the storageDaysInterval observable. If data is not enough to show
     * at least 6 periods, it will use storageDaysInterval as 1, it means
     * that will show daily stats.
     */
    @computed
    get trafficForChart() {
        const traffic = toJS(this.traffic);
        const days = this.storageDaysInterval;
        let result = [];
        let shouldReverse = false;
        if (days === 1 || days > traffic.length / 6) {
            result = traffic;
        } else {
            shouldReverse = true;
            let mod = traffic.length % days;

            for (let i = traffic.length - 1; i >= mod; i = i - days) {
                let res = {
                    used: 0,
                    unit: 'byte',
                    year: traffic[i].year,
                    month: traffic[i].month,
                    day: traffic[i].day,
                };
                let usageAcum = 0;
                for (let j = 0; j < days; j++) {
                    usageAcum = usageAcum + traffic[i - j].used;
                }
                res.used = usageAcum;
                result.push(res);
            }
        }
        let final = map(result, day => {
            let date = moment();
            date.month(day.month - 1)
                .year(day.year)
                .date(day.day);
            return {
                name: date.format('MMM D'),
                traffic: day.used,
            };
        });

        return shouldReverse ? reverse(final) : final;
    }
}

export default new QuotaStore();
